<script lang="ts" setup>
import { computed, ref } from "vue";
import { Modal } from "@/components";
import {
  useCreateDocumentSpaceCrmMappingMutation,
  useUpdateDocumentSpaceNameMutation,
  CrmContact,
} from "@/graphql/operations";
import { useI18n } from "vue-i18n";
import ContactPicker from "./ContactPicker.vue";

const { t } = useI18n({});

const props = defineProps({
  documentSpaceId: {
    required: true,
    type: String,
  },
  documentSpaceName: {
    required: true,
    type: String,
  },
  inheritClassicTheme: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close", "success", "error"]);
const emitClose = () => {
  if (saving.value) return;
  // Mapping creation is committed even when the following rename failed.
  if (mappedContactId.value) emitSuccess(mappedContactId.value);
  emit("close");
};
const emitSuccess = (contactId: string) => emit("success", contactId);
const emitError = () => emit("error");

const selectedContact = ref<CrmContact | null>(null);

const changeSelection = (contact: CrmContact) => {
  if (saving.value || mappedContactId.value) return;
  selectedContact.value = contact;
};

const submitDisabled = computed(() => !selectedContact.value || saving.value);
const saveError = ref(false);

const updateName = ref<boolean>(true);

const mappedContactId = ref<string>();
const mappedName = ref<string>();
const saving = ref(false);
const { executeMutation: updateSpaceName } =
  useUpdateDocumentSpaceNameMutation();
const { executeMutation } = useCreateDocumentSpaceCrmMappingMutation();
const submit = async () => {
  if (!selectedContact.value || saving.value) {
    return;
  }

  saveError.value = false;
  saving.value = true;
  try {
    if (!mappedContactId.value) {
      const contact = selectedContact.value;
      const name = updateName.value
        ? formatName(contact)
        : props.documentSpaceName;
      const result = await executeMutation({
        spaceId: props.documentSpaceId,
        crmContactId: contact.id,
        name,
      });
      // GraphQL root mutations can commit independently. Never insert the same
      // mapping again after the server confirmed its creation.
      if (result.data?.createDocumentSpaceCrmMapping) {
        mappedContactId.value = contact.id;
        mappedName.value = name;
      }
      if (!mappedContactId.value || !result.data?.updateDocumentSpace) {
        saveError.value = true;
        emitError();
        return;
      }
    } else {
      const result = await updateSpaceName({
        id: props.documentSpaceId,
        name: mappedName.value!,
      });
      if (result.error || !result.data?.updateDocumentSpace) {
        saveError.value = true;
        emitError();
        return;
      }
    }
  } finally {
    saving.value = false;
  }
  emitClose();
};

const formatName = (contact: CrmContact) => {
  const nameParts = [contact.lastName];
  if (contact.firstName) {
    nameParts.push(contact.firstName);
  }

  let name = nameParts.join(", ");
  return name;
};
</script>

<template>
  <modal
    :active="true"
    @close="emitClose"
    :scrolling="true"
    :closeDisabled="saving"
    class="large adaptive_footer crm_contact_modal"
  >
    <template #header> CRM Integration: Select a contact </template>

    <template #body>
      <p v-if="saveError" class="save_error" role="alert">
        {{ $t(mappedContactId ? "crmContactSearch.renameFailed" : "error") }}
      </p>
      <div :inert="saving || !!mappedContactId" :aria-busy="saving">
        <ContactPicker
          :inherit-classic-theme="props.inheritClassicTheme"
          @submit="submit"
          @change="changeSelection"
        />
      </div>
    </template>

    <template #footer>
      <div class="checkbox_with_label">
        <div class="checkbox_container">
          <input
            type="checkbox"
            class="checkbox"
            id="checkbox"
            v-model="updateName"
            :disabled="saving || !!mappedContactId"
          />
        </div>
        <label for="checkbox">{{
          $t("crmContactSearch.updateName.label")
        }}</label>
      </div>
      <div class="buttons">
        <button
          :class="{ button: true, primary: true, disabled: submitDisabled }"
          :disabled="submitDisabled"
          @click="submit"
        >
          {{ $t("submit") }}
        </button>
        <button class="button secondary" :disabled="saving" @click="emitClose">
          {{ $t("cancel") }}
        </button>
      </div>
    </template>
  </modal>
</template>

<style scoped lang="scss">
.save_error {
  color: var(--classic-danger, #c62828);
  font-size: 0.9em;
}
$checkbox-width: 40px;
$color-secondary: #34b5ba;

.checkbox_container {
  vertical-align: middle;
  padding: 0.8em 0.8em 0.8em 0.5em;
  cursor: pointer;
}

.crm_contact_modal :deep(.footer) {
  display: flex;
  align-items: center;
  gap: 1em;
}

.checkbox_with_label {
  flex: 1 1 auto;
  min-width: 0;
  line-height: 1.4;
}

.modal .footer .buttons {
  position: static;
  width: auto;
  flex: 0 0 auto;
  white-space: nowrap;
}

.checkbox_with_label .checkbox_container {
  padding: 0;
  margin-right: 0.5em;
}

.body_container {
  height: 100%;
}
</style>
